import { useState, useEffect } from 'react';

/**
 * iPadOS 13+ reports the *desktop* user agent ("Macintosh; Intel Mac OS X"), so /ipad/ never
 * matches on a modern iPad. A real Mac reports maxTouchPoints 0; only an iPad claims to be a
 * Macintosh with a touch screen. Without this an iPad falls through to the desktop layout —
 * hover-only row actions and all — because its width is also >= 1024.
 */
const isIPadOS = (userAgent: string): boolean =>
  /macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;

/**
 * Detect mobile devices using multiple parameters.
 * Combines touch capability, pointer type, hover capability, screen dimensions, and user agent.
 * This provides more accurate detection than screen width alone, especially for high-resolution
 * mobile devices.
 */
function detectIsMobile(): boolean {
  if (typeof window === 'undefined') return false;

  // Check 1: Touch capability
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Check 2: Pointer type (coarse indicates touch screen)
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  // Check 3: Hover capability (none indicates touch device)
  const hasNoHover = window.matchMedia('(hover: none)').matches;

  // Check 4: Screen dimensions and aspect ratio
  const width = window.innerWidth;
  const height = window.innerHeight;
  const aspectRatio = height / width;
  // Mobile devices typically have width < 1024px OR (width < 1440px AND high aspect ratio > 1.5)
  const hasMobileDimensions = width < 1024 || (width < 1440 && aspectRatio > 1.5);

  // Check 5: User agent fallback (check for common mobile device patterns)
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileUserAgentPatterns = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const hasMobileUserAgent = mobileUserAgentPatterns.test(userAgent) || isIPadOS(userAgent);

  // Combine checks: device is mobile if it has touch AND (coarse pointer OR no hover OR iPad) AND
  // (mobile dimensions OR mobile user agent). iPad with a trackpad reports fine pointer + hover,
  // so isIPadOS must bypass that gate or the tablet falls through to desktop layout.
  const hasTouchUi = hasCoarsePointer || hasNoHover || isIPadOS(userAgent);
  return hasTouch && hasTouchUi && (hasMobileDimensions || hasMobileUserAgent);
}

export { detectIsMobile };

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(detectIsMobile);

  useEffect(() => {
    // Update on window resize and orientation change
    const updateIsMobile = () => {
      setIsMobile(detectIsMobile());
    };

    // Initial check
    updateIsMobile();

    // Listen to media query changes
    const pointerMediaQuery = window.matchMedia('(pointer: coarse)');
    const hoverMediaQuery = window.matchMedia('(hover: none)');

    // Media query change handlers
    const handlePointerChange = () => updateIsMobile();
    const handleHoverChange = () => updateIsMobile();

    // Add listeners
    if (pointerMediaQuery.addEventListener) {
      pointerMediaQuery.addEventListener('change', handlePointerChange);
    } else {
      // Fallback for older browsers
      pointerMediaQuery.addListener(handlePointerChange);
    }

    if (hoverMediaQuery.addEventListener) {
      hoverMediaQuery.addEventListener('change', handleHoverChange);
    } else {
      // Fallback for older browsers
      hoverMediaQuery.addListener(handleHoverChange);
    }

    // Listen to resize and orientation change
    window.addEventListener('resize', updateIsMobile);
    window.addEventListener('orientationchange', updateIsMobile);

    // Cleanup
    return () => {
      if (pointerMediaQuery.removeEventListener) {
        pointerMediaQuery.removeEventListener('change', handlePointerChange);
      } else {
        pointerMediaQuery.removeListener(handlePointerChange);
      }

      if (hoverMediaQuery.removeEventListener) {
        hoverMediaQuery.removeEventListener('change', handleHoverChange);
      } else {
        hoverMediaQuery.removeListener(handleHoverChange);
      }

      window.removeEventListener('resize', updateIsMobile);
      window.removeEventListener('orientationchange', updateIsMobile);
    };
  }, []);

  return isMobile;
}


